import { autoUpdate, offset, shift, useFloating } from "@floating-ui/react";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
    faArrowRightFromBracket,
    faBuildingColumns,
    faBuildings,
    faBullseyeArrow,
    faChartArea,
    faChevronsLeft,
    faChevronsRight,
    faCog,
    faDownload,
    faMap,
    faPuzzle,
    faQuestionCircle,
    faTriangleExclamation,
    faUpload,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMicrochipAi } from "@fortawesome/sharp-light-svg-icons";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { NavLink, useNavigate } from "react-router-dom";
import aershedLogo from "./../../assets/img/aershed-logo-horizontal.png";
import aershedLogoSmall from "./../../assets/img/aershed-symbol.png";
import { useAppSelector } from "../../hooks";
import { ROUTES } from "../../routes";

const sidebarState = atomWithStorage<boolean>("sidebarState", false);

interface SidebarMenuProps {
    icon: IconDefinition;
    to: string;
    text: string;
    expanded: boolean;
}

const SidebarButton = (props: SidebarMenuProps) => {
    return (
        <NavLink
            to={props.to}
            title={props.text}
            className={({ isActive }) => {
                return `
                    px-3 py-2 w-full rounded-lg flex items-center whitespace-nowrap
                    transition-all gap-3 ${props.expanded ? "justify-start" : "justify-center"} ${
                        isActive
                            ? "bg-ae-blue-900 text-stone-50 font-bold"
                            : "text-ae-blue-900 hover:bg-neutral-200"
                    }
                `;
            }}
        >
            <FontAwesomeIcon icon={props.icon} className="h-5 w-5" />
            <h2
                className={`flex-grow font-medium ${
                    props.expanded ? "" : "hidden"
                }`}
            >
                {props.text}
            </h2>
        </NavLink>
    );
};

export const SideMenu = () => {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useAtom(sidebarState);

    // Expand/contract floating
    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "right",
        middleware: [
            offset({
                mainAxis: -12,
            }),
            shift(),
        ],
        whileElementsMounted: autoUpdate,
    });

    // User specific information
    const isStaff = useAppSelector((state) => state.auth.isStaff);
    const viewAsCompany = useAppSelector((state) => state.auth.viewAsCompany);
    const isTemporaryUser = useAppSelector(
        (state) => state.auth.isTemporaryUser,
    );
    const firstName = useAppSelector((state) => state.auth.firstName);
    const lastName = useAppSelector((state) => state.auth.lastName);

    // Permissions
    const isSuperuser = useAppSelector((state) => state.auth.isSuperuser);
    const userPermissions = useAppSelector((state) => state.auth.permissions);
    const flags = useAppSelector((state) => state.auth.flags);

    return (
        <div
            ref={refs.setReference}
            className={`
                ${expanded ? "w-54" : "w-16"} h-screen
                bg-ae-neutral-50 border-r border-slate-200
                flex flex-col p-2 transition-all group relative
                overflow-y-scroll overflow-x-hidden text-base
            `}
        >
            {/* Expand/collapse controls */}
            <button
                type="button"
                ref={refs.setFloating}
                title={expanded ? "Minimize menu" : "Expand menu"}
                style={floatingStyles}
                className="relative hidden group-hover:block z-50 rounded-r bg-ae-neutral-50 w-6"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="absolute right-0 w-1/2 h-full border border-slate-200 border-l-0 rounded-r" />
                <div className="flex items-center justify-center rounded h-12 hover:bg-ae-neutral-200 w-full">
                    {expanded ? (
                        <FontAwesomeIcon
                            icon={faChevronsLeft}
                            className="w-3"
                        />
                    ) : (
                        <FontAwesomeIcon
                            icon={faChevronsRight}
                            className="w-3"
                        />
                    )}
                </div>
            </button>

            {/* Header */}
            <div className="mt-4 mb-10 flex items-center justify-evenly">
                <button
                    type="button"
                    onClick={() => {
                        navigate(ROUTES.ROOT);
                    }}
                >
                    <img
                        alt="AERSHED"
                        className="max-h-10"
                        src={expanded ? aershedLogo : aershedLogoSmall}
                    />
                </button>
            </div>

            {/* Main navigation pages */}
            <div className="flex-grow flex flex-col gap-1 mb-1">
                {/* Dashboards */}
                <SidebarButton
                    to={ROUTES.DASHBOARD_ALL}
                    icon={faBullseyeArrow}
                    text="Emissions"
                    expanded={expanded}
                />
                {/* Map */}
                {userPermissions.mapView && (
                    <SidebarButton
                        to={ROUTES.MAP}
                        icon={faMap}
                        text="Maps"
                        expanded={expanded}
                    />
                )}
                {/* Analytics */}
                <SidebarButton
                    to={ROUTES.ANALYTICS}
                    icon={faChartArea}
                    text="Analytics"
                    expanded={expanded}
                />
                {userPermissions.infrastructureView && (
                    <SidebarButton
                        to={ROUTES.INFRASTRUCURE}
                        icon={faBuildings}
                        text="Infrastructure"
                        expanded={expanded}
                    />
                )}
                {userPermissions.dataUploadsView && (
                    <SidebarButton
                        to={ROUTES.DATAUPLOADS}
                        icon={faUpload}
                        text="Data upload"
                        expanded={expanded}
                    />
                )}
                {userPermissions.dataDownloadView && (
                    <SidebarButton
                        to={ROUTES.DATADOWNLOAD}
                        icon={faDownload}
                        text="Data download"
                        expanded={expanded}
                    />
                )}
            </div>

            {/* Items attached to bottom of page */}
            <div>
                <div className="flex flex-col mb-8 gap-1">
                    {flags.includes("show_aersage_link") && (
                        <SidebarButton
                            to={ROUTES.AERSAGE}
                            icon={faMicrochipAi}
                            text="AERSAGE AI"
                            expanded={expanded}
                        />
                    )}
                    {(isStaff || viewAsCompany) && (
                        <SidebarButton
                            to={ROUTES.ADMIN_DASHBOARD}
                            icon={faPuzzle}
                            text="Administration"
                            expanded={expanded}
                        />
                    )}
                    <SidebarButton
                        to={ROUTES.SUPPORT}
                        icon={faQuestionCircle}
                        text="Help & Support"
                        expanded={expanded}
                    />
                    {/* Temporary "view as" users have no settings to manage */}
                    {!isTemporaryUser && (
                        <SidebarButton
                            to={ROUTES.SETTINGS_HOME}
                            icon={faCog}
                            text="Settings"
                            expanded={expanded}
                        />
                    )}
                    <SidebarButton
                        to={ROUTES.LICENSING}
                        icon={faBuildingColumns}
                        text="Licensing"
                        expanded={expanded}
                    />
                    {!expanded && (
                        <SidebarButton
                            to={ROUTES.LOGOUT}
                            icon={faArrowRightFromBracket}
                            text="Logout"
                            expanded={false}
                        />
                    )}
                </div>

                {/* User header */}
                {expanded && (
                    <div className="px-4 mb-4 flex justify-evenly items-center text-ae-blue-900">
                        <div className="grow mr-4">
                            {`${firstName} ${lastName}`}
                        </div>

                        <SidebarButton
                            to={ROUTES.LOGOUT}
                            icon={faArrowRightFromBracket}
                            text="Logout"
                            expanded={false}
                        />
                    </div>
                )}

                {/* Superuser account warning */}
                {isStaff && isSuperuser && (
                    <p className="flex bg-red-500 justify-center p-2 rounded text-white text-sm">
                        {expanded ? (
                            "You are using a superuser account!"
                        ) : (
                            <FontAwesomeIcon
                                icon={faTriangleExclamation}
                                className="h-5"
                            />
                        )}
                    </p>
                )}
            </div>
        </div>
    );
};
